/** @jsx jsx */
import React, { useCallback, useMemo } from 'react';
import kebabCase from 'lodash/kebabCase';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { useBoardId } from '@jira/project-settings__board-settings-common/src/useBoardId.tsx';
import { Anchor, xcss, Text, Stack } from '@atlaskit/primitives';
import {
	Anchor as CompiledAnchor,
	Stack as CompiledStack,
	Text as CompiledText,
} from '@atlaskit/primitives/compiled';
import Link from '@atlaskit/link';
import Heading from '@atlaskit/heading';
import Tooltip from '@atlaskit/tooltip';
import Lozenge from '@atlaskit/lozenge';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useContainerContext } from '@atlassian/jira-providers-container-context/src/main.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import type { reportCard_reportsOverviewPage$key } from '@atlassian/jira-relay/src/__generated__/reportCard_reportsOverviewPage.graphql';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { ReportImage } from '../report-image/index.tsx';
import { CI_CD_GET_STARTED_LINK, CYCLE_TIME_REPORT_KEY } from '../../common/constants.tsx';
import messages from '../../messages.tsx';
import { ReportCardImage } from './image.tsx';

type ReportCardProps = {
	report: reportCard_reportsOverviewPage$key;
	/**
	 * Whether the 'new' design is visible.
	 *
	 * This is set by feature gate
	 * uxissue_545_migrate_tmp_reports_overview_page
	 * */
	isRedesignVisible?: boolean;
};

const useTMPReportUrl = (reportKey: string) => {
	const { data: projectData } = useProjectContext();
	const boardId = useBoardId();

	const projectKey = projectData?.projectKey;

	if (!projectKey || !boardId) {
		return undefined;
	}

	if (reportKey === 'burndown') {
		return `/jira/software/projects/${projectKey}/boards/${boardId}/reports/${reportKey}?source=overview`;
	}

	return `/jira/software/projects/${projectKey}/boards/${boardId}/reports/${reportKey}`;
};

const usePrettyReportUrl = (url: string) => {
	const [{ data, error }] = useContainerContext();

	const urlObj = new URL(url, globalThis.window.location.origin);
	const queryParams = Object.fromEntries(urlObj.searchParams.entries());

	if (!data || error || !queryParams.view) {
		return url;
	}
	let prettyUrl = '';
	// @ts-expect-error - TS2339 - Property 'project' does not exist on type 'ContainerContext'.
	if (!data.project) {
		// @ts-expect-error - TS2339 - Property 'user' does not exist on type 'ContainerContext'.
		prettyUrl += `/jira/people/${data.user.accountId}`;
	} else {
		// @ts-expect-error - TS2339 - Property 'project' does not exist on type 'ContainerContext'.
		prettyUrl += `/jira/software/c/projects/${data.project.key}`;
	}
	prettyUrl += `/boards/${data.scope?.id}/reports/${kebabCase(queryParams.chart)}`;
	return prettyUrl;
};

export const ReportCard = ({ report, isRedesignVisible }: ReportCardProps): JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data: projectData } = useProjectContext();
	const intl = useIntl();

	const reportCardData = useFragment<reportCard_reportsOverviewPage$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment reportCard_reportsOverviewPage on JiraReport {
				...reportImage_reportsOverviewPage
				...image_reportsOverviewPage_ReportCardImage
				name @required(action: THROW)
				description @required(action: THROW)
				url @required(action: THROW)
				key @required(action: THROW)
				isNotApplicableReason @include(if: $isMigrateTMPReportsOverviewEnabled)
				isNotApplicableDesc @include(if: $isMigrateTMPReportsOverviewEnabled)
			}
		`,
		report,
	);

	const name = reportCardData.name;
	const description = reportCardData.description;
	const isTeamManaged = projectData?.simplified || false;

	const prettyUrl = usePrettyReportUrl(reportCardData.url);
	const tmpUrl = useTMPReportUrl(reportCardData.url);

	const url = useMemo(() => {
		if (isTeamManaged && tmpUrl && fg('uxissue_545_migrate_tmp_reports_overview_page')) {
			return tmpUrl;
		}
		return prettyUrl;
	}, [isTeamManaged, tmpUrl, prettyUrl]);

	const { isNotApplicableReason, isNotApplicableDesc, key: reportKey } = reportCardData;

	const fireReportClickedAnalytics = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'reportButton', {
			report: name,
		});
	}, [createAnalyticsEvent, name]);

	const handleClick = useCallback(() => {
		fireReportClickedAnalytics();
	}, [fireReportClickedAnalytics]);

	if (isRedesignVisible) {
		return (
			<div css={[styles.root, !isNotApplicableReason && styles.rootHover]}>
				<CompiledStack space="space.100">
					<div css={styles.imageContainer}>
						<ReportCardImage report={reportCardData} />
						{isNotApplicableReason ? (
							<div css={styles.lozenge}>
								<Tooltip content={`${isNotApplicableReason}. ${isNotApplicableDesc}`}>
									<Lozenge appearance="moved" isBold>
										{isNotApplicableReason}
									</Lozenge>
								</Tooltip>
							</div>
						) : null}
					</div>
					{isNotApplicableReason ? (
						<Heading size="xsmall" as="h3">
							{name}
						</Heading>
					) : (
						<CompiledAnchor href={url} onClick={handleClick} aria-label={name} xcss={styles.link}>
							<Heading size="xsmall" as="h3">
								{name}
							</Heading>
						</CompiledAnchor>
					)}
					<CompiledText size="medium" as="p">
						{description}
					</CompiledText>
					{isNotApplicableReason && reportKey === CYCLE_TIME_REPORT_KEY && (
						<Link href={CI_CD_GET_STARTED_LINK} target="_blank">
							{intl.formatMessage(messages.learnMore)}
						</Link>
					)}
				</CompiledStack>
			</div>
		);
	}

	return (
		<Anchor xcss={oldCardStyles} href={url} onClick={handleClick} aria-label={name}>
			<Stack space="space.100">
				<ReportImage report={reportCardData} />
				<Heading size="xsmall" as="h3">
					{name}
				</Heading>
				<Text size="medium" as="p">
					{description}
				</Text>
			</Stack>
		</Anchor>
	);
};

const styles = cssMap({
	root: {
		position: 'relative',
		boxSizing: 'border-box',
		height: '320px',
		width: '270px',
		display: 'flex',
		overflowY: 'auto',
		flexDirection: 'column',
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		backgroundColor: token('elevation.surface.overlay'),
	},
	rootHover: {
		'&:hover': {
			borderColor: token('color.border.focused'),
			textDecoration: 'none',
		},
		'&:focus-within': {
			borderColor: token('color.border.focused'),
		},
	},
	link: {
		position: 'initial',
		textDecoration: 'none',
		'&::after': {
			bottom: 0,
			content: '""',
			left: 0,
			position: 'absolute',
			right: 0,
			top: 0,
		},
		'&:hover': {
			textDecoration: 'none',
		},
		'&:focus': {
			textDecoration: 'none',
			outline: 'none',
		},
		'&:focus-visible': {
			textDecoration: 'none',
			outline: 'none',
		},
	},
	lozenge: {
		position: 'absolute',
		bottom: token('space.100'),
		right: token('space.100'),
	},
	imageContainer: {
		position: 'relative',
	},
});

const oldCardStyles = xcss({
	height: '320px',
	width: '270px',
	display: 'flex',
	flexDirection: 'column',
	overflowX: 'hidden',
	overflowY: 'hidden',
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
	border: '2px solid',
	borderColor: 'color.border',
	borderRadius: 'radius.large',
	backgroundColor: 'elevation.surface.overlay',
	':hover': {
		borderColor: 'color.border.focused',
		borderStyle: 'solid',
		borderWidth: 'space.025',
		overflowY: 'auto',
		textDecoration: 'none',
	},
	':focus-within': {
		borderColor: 'color.border.focused',
		borderStyle: 'solid',
		borderWidth: 'space.025',
		overflowY: 'auto',
		outline: 'none',
		textDecoration: 'none',
	},
	textDecoration: 'none',
});
